import { Position } from 'reactflow';

/** Pixels between stacked self-relation loops (same node → same node). */
export const SELF_EDGE_LANE_GAP = 36;

/**
 * Path for edges where source === target (self-relation).
 * Routes outside the node so the stroke is not hidden behind the entity.
 * Based on https://reactflow.dev/examples/edges/custom-edges (SelfConnectingEdge).
 *
 * When several self-edges share the same node, pass `laneIndex` / `laneCount` so
 * each loop is offset and lines do not overlap.
 *
 * @param {object} params
 * @param {string} params.sourceId
 * @param {number} params.sourceX
 * @param {number} params.sourceY
 * @param {number} params.targetX
 * @param {number} params.targetY
 * @param {import('reactflow').Position} params.sourcePosition
 * @param {import('reactflow').Position} params.targetPosition
 * @param {(id: string) => import('reactflow').Node | undefined} params.getNode
 * @param {number} [params.laneIndex] 0-based index among self-edges on this node (stable sort by edge id)
 * @param {number} [params.laneCount]
 * @returns {{ path: string, labelX: number, labelY: number }}
 */
export function getSelfConnectingPath({
  sourceId,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  getNode,
  laneIndex = 0,
  laneCount = 1,
}) {
  const node = getNode(sourceId);
  const abs = node?.positionAbsolute;
  const h = node?.height;

  let baseLoopY;
  if (abs != null && typeof h === 'number' && h > 0) {
    baseLoopY = abs.y - 56;
  } else {
    baseLoopY = Math.min(sourceY, targetY) - 150;
  }

  const safeCount = Math.max(1, laneCount);
  const safeIdx = Math.min(Math.max(0, laneIndex), safeCount - 1);
  const laneOffset = safeIdx * SELF_EDGE_LANE_GAP;

  const isVerticalHandles =
    (sourcePosition === Position.Top && targetPosition === Position.Bottom) ||
    (sourcePosition === Position.Bottom && targetPosition === Position.Top);

  if (isVerticalHandles) {
    const baseLoopX = Math.max(sourceX, targetX) + 120;
    const loopX = baseLoopX + laneOffset;
    const path = `M ${sourceX} ${sourceY} L ${loopX} ${sourceY} L ${loopX} ${targetY} L ${targetX} ${targetY}`;
    return {
      path,
      labelX: loopX,
      labelY: (sourceY + targetY) / 2,
    };
  }

  const loopY = baseLoopY - laneOffset;
  const path = `M ${sourceX} ${sourceY} L ${sourceX} ${loopY} L ${targetX} ${loopY} L ${targetX} ${targetY}`;
  return {
    path,
    labelX: (sourceX + targetX) / 2,
    labelY: loopY,
  };
}
